import React, { useState, useEffect, useMemo } from 'react';
import { CheckSquare, Square, ClipboardList, ChevronDown, ChevronUp } from 'lucide-react';

export default function CarrierChecklist({ carrier, callReason, client, checklistState, onChecklistChange }) {
  const [expanded, setExpanded] = useState(true);

  // Get checklist items for this carrier + call reason
  const items = useMemo(() => {
    if (!carrier?.checklists || !callReason) return [];
    const checklists = typeof carrier.checklists === 'string'
      ? JSON.parse(carrier.checklists)
      : carrier.checklists;
    return checklists[callReason] || checklists['general'] || [];
  }, [carrier, callReason]);

  // Initialize state from items or from persisted state
  const [checked, setChecked] = useState(() => {
    if (checklistState && checklistState.length > 0) return checklistState;
    return items.map((item) => ({
      label: item.label,
      checked: false,
      value: item.auto_fill && client ? (client[item.auto_fill] || '') : '',
    }));
  });

  // Re-initialize when carrier or callReason changes, preserving checked state for matching items
  useEffect(() => {
    if (!items || items.length === 0) return;
    setChecked(prev => {
      // Build a lookup from the previous state by label for fast access
      const prevByLabel = {};
      prev.forEach(p => { prevByLabel[p.label] = p; });
      // Build new state, preserving existing checked/value where label matches
      return items.map((item) => {
        const existing = prevByLabel[item.label];
        return existing || {
          label: item.label,
          checked: false,
          value: item.auto_fill && client ? (client[item.auto_fill] || '') : '',
        };
      });
    });
  }, [items, carrier?.id, callReason]);

  // Notify parent of changes
  useEffect(() => {
    if (onChecklistChange && checked.length > 0) {
      onChecklistChange(checked);
    }
  }, [checked, onChecklistChange]);

  if (items.length === 0) return null;

  const completedCount = checked.filter(c => c.checked).length;
  const allDone = completedCount === checked.length;

  function toggleItem(index) {
    setChecked(prev => prev.map((item, i) =>
      i === index ? { ...item, checked: !item.checked } : item
    ));
  }

  function updateValue(index, value) {
    setChecked(prev => prev.map((item, i) =>
      i === index ? { ...item, value } : item
    ));
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors"
      >
        <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
          <ClipboardList size={15} className="text-violet-500" />
          Call Checklist
          <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
            allDone ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
          }`}>
            {completedCount}/{checked.length}
          </span>
        </h3>
        {expanded ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
      </button>

      {expanded && (
        <div className="p-3 space-y-1.5">
          {checked.map((item, i) => (
            <div key={i} className="flex items-start gap-2 group">
              <button
                onClick={() => toggleItem(i)}
                className="mt-0.5 flex-shrink-0"
              >
                {item.checked ? (
                  <CheckSquare size={16} className="text-emerald-500" />
                ) : (
                  <Square size={16} className="text-slate-300 group-hover:text-slate-400" />
                )}
              </button>
              <div className="flex-1 min-w-0">
                <span className={`text-xs font-medium ${item.checked ? 'text-slate-400 line-through' : 'text-slate-700'}`}>
                  {item.label}
                </span>
                {!item.checked && (
                  <input
                    type="text"
                    value={item.value}
                    onChange={e => updateValue(i, e.target.value)}
                    placeholder="..."
                    className="block w-full mt-0.5 px-2 py-1 text-[11px] bg-slate-50 border border-slate-150 rounded focus:ring-1 focus:ring-violet-200 focus:border-violet-300 outline-none font-mono"
                  />
                )}
                {item.checked && item.value && (
                  <span className="block text-[11px] text-slate-400 font-mono truncate">{item.value}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
